import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cards, X, ArrowCounterClockwise, SealCheck, ArrowsClockwise } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FlashcardReviewButton = ({ cards, folder, isDe, onDone }) => {
  const [open, setOpen] = useState(false);
  const deck = cards.filter(c => !c.learned);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={!deck.length}
        title={deck.length ? '' : (isDe ? 'Alle Karten gelernt' : 'Todas aprendidas')}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-semibold transition-colors disabled:bg-stone-300 disabled:cursor-not-allowed"
        data-testid="flashcard-review-open-btn"
      >
        <Cards size={18} weight="fill" />
        {isDe ? 'Üben' : 'Repasar'}{folder ? ` „${folder}“` : ''} · {deck.length}
      </button>
      {open && <ReviewSession deck={deck} folder={folder} isDe={isDe} onClose={() => { setOpen(false); onDone?.(); }} />}
    </>
  );
};

const ReviewSession = ({ deck, folder, isDe, onClose }) => {
  const [queue, setQueue] = useState(() => [...deck].sort(() => Math.random() - 0.5));
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(0);
  const [knownIds, setKnownIds] = useState(() => new Set());
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState(false);
  const card = queue[0];

  const next = () => { setFlipped(false); setQueue(q => q.slice(1)); };
  const markKnown = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/flashcards/${card.card_id}`, { learned: true }, { withCredentials: true });
      setKnown(k => k + 1);
      setKnownIds(s => new Set(s).add(card.card_id));
      next();
    } catch { toast.error('Fehler'); }
    finally { setBusy(false); }
  };
  const repeat = () => {
    setAgain(a => a + 1);
    setFlipped(false);
    setQueue(q => [...q.slice(1), q[0]]);
  };
  const restart = () => { setQueue(deck.filter(c => !knownIds.has(c.card_id)).sort(() => Math.random() - 0.5)); setKnown(0); setAgain(0); setFlipped(false); };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F3A44]/95 flex flex-col items-center justify-center p-4" data-testid="flashcard-review-session">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:bg-white/10" data-testid="flashcard-review-close-btn"><X size={24} /></button>
      <p className="text-white/70 text-xs uppercase tracking-widest font-bold mb-4" data-testid="flashcard-review-progress">
        {folder || (isDe ? 'Alle Karten' : 'Todas')} · {isDe ? 'noch' : 'quedan'} {queue.length}
      </p>
      {card ? (
        <>
          <button onClick={() => setFlipped(f => !f)} className="w-full max-w-md aspect-[3/2] [perspective:1200px]" data-testid="flashcard-review-card">
            <div className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] ${flipped ? '[transform:rotateY(180deg)]' : ''}`}>
              <div className="absolute inset-0 [backface-visibility:hidden] bg-white rounded-3xl shadow-2xl flex flex-col items-center justify-center p-6">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#AB1C24] mb-3">{card.direction?.replace('->', ' → ')}</span>
                <span className="font-heading text-3xl sm:text-4xl font-bold text-[#292524] text-center" data-testid="flashcard-review-front">{card.word}</span>
                <span className="text-xs text-[#A8A29E] mt-6">{isDe ? 'Tippen zum Umdrehen' : 'Toca para girar'}</span>
              </div>
              <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] bg-[#FDFBF7] rounded-3xl shadow-2xl flex flex-col items-center justify-center p-6 border-4 border-[#EAB308]">
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[#1F3A44] text-center" data-testid="flashcard-review-back">{card.translation}</span>
                {card.example_sentence && <span className="text-sm italic text-[#57534E] mt-4 text-center">„{card.example_sentence}“</span>}
              </div>
            </div>
          </button>
          <div className={`flex gap-3 mt-6 w-full max-w-md transition-opacity ${flipped ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <button onClick={repeat} disabled={busy} className="flex-1 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center justify-center gap-2" data-testid="flashcard-review-again-btn">
              <ArrowCounterClockwise size={18} /> {isDe ? 'Nochmal' : 'Otra vez'}
            </button>
            <button onClick={markKnown} disabled={busy} className="flex-1 py-3 rounded-2xl bg-[#EAB308] hover:bg-[#CA9A06] text-[#1F3A44] font-bold flex items-center justify-center gap-2" data-testid="flashcard-review-known-btn">
              <SealCheck size={18} weight="fill" /> {isDe ? 'Kann ich!' : '¡Me la sé!'}
            </button>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4" data-testid="flashcard-review-summary">
          <SealCheck size={48} weight="fill" className="text-[#EAB308] mx-auto" />
          <h3 className="font-heading text-2xl font-bold text-[#292524]">{isDe ? 'Runde geschafft!' : '¡Ronda completada!'}</h3>
          <p className="text-sm text-[#57534E]">
            <span className="font-bold text-emerald-700" data-testid="flashcard-review-known-count">{known}</span> {isDe ? 'gelernt (bleiben in ihrem Ordner, Tab „Aprendidas“)' : 'aprendidas (siguen en su carpeta, en «Aprendidas»)'} · {again}× {isDe ? 'wiederholt' : 'repetidas'}
          </p>
          <div className="flex gap-2">
            <button onClick={restart} className="flex-1 py-2.5 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold flex items-center justify-center gap-1.5" data-testid="flashcard-review-restart-btn">
              <ArrowsClockwise size={16} /> {isDe ? 'Neue Runde' : 'Otra ronda'}
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-semibold" data-testid="flashcard-review-finish-btn">
              {isDe ? 'Fertig' : 'Terminar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
